import React, { Fragment } from 'react'
import { Provider } from 'react-redux'
import { ThemeProvider } from 'styled-components'
import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom'

import store from './redux/store'
import * as theme from './utils/theme'
import { GlobalText } from './components'
import DashboardLayout from './layout/Dashboard'
import OnboardingLayout from './layout/Onboarding'
import l from './config/localization'
import './assets/fonts/fonts.css'

import { NavigationProvider } from 'utils/searchFilterHelpers'

import {
  Terms,
  HomePage,
  PrivacyPolicy,
  NotFound,
  DocsPage,
  Login,
  Values,
  EmailConfirmation,
  Probation,
  PasswordReset,
  PasswordSetup,
  OnboardMentor,
  OnboardPrincipal,
  Campaign,
  Opportunity,
  Organisation,
  User,
  HostFeedback,
  VolunteerFeedback,
  Dashboard,
  DashboardEmailActivity,
  DashboardOpportunities,
  DashboardNewRounds,
  DashboardNewWorkshops,
  DashboardWorkshop,
  DashboardRound,
  DashboardCampaign,
  DashboardCampaigns,
  DashboardNewCampaign,
  DashboardInstitutionNew,
  DashboardInstitutions,
  DashboardInstitution,
  DashboardOrganization,
  DashboardSchoolLearners,
  DashboardOrganisationMembers,
  DashboardOrganizations,
  DashboardNewOrganization,
  DashboardNewPeople,
  DashboardPeopleIndex,
  DashboardUpdateMembership,
  DashboardPersonActivity,
  DashboardAccount,
  DashboardUserAccountPrefs,
  DashboardNotFound,
  JoinBrowniePoints,
  CompleteOnboarding,
  GrowsmartMentors,
  GrowsmartRsvps,
  GrowsmartRegistrations,
  GrowsmartManageRound,
  GrowsmartEnrolment,
  GrowsmartEnrolments,
  GrowsmartSponsorship,
  GrowsmartSponsorshipNew,
  GrowsmartSponsorships,
  GrowsmartScholarship,
  GrowsmartScholarshipNew,
  GrowsmartScholarships,
  DashboardScholarshipApplication,
  GrowsmartScholarshipApplications,
  GrowsmartScholarshipNewApplication,
  GrowsmartInviteSchools,
  GrowsmartCompetitionOverview,
  GrowsmartHardcopySubmissions,
  GrowsmartInvitationDecline,
  GrowsmartParentInfo
} from './routes'

const App = () => (
  <ThemeProvider theme={theme}>
    <Provider store={store}>
      <Fragment>
        <GlobalText />
        <Router>
          <NavigationProvider>
            <Routes>
              <Route index element={<HomePage />} />
              <Route path="/docs" element={<DocsPage />} />

              {/* Entity pages */}
              <Route path={`${l('ROUTE_CAMPS')}/:campaignId`} element={<Campaign />} />
              <Route path={`${l('ROUTE_ORGS')}/:orgName`} element={<Organisation />} />
              <Route path="/users/:userId" element={<User />} />

              {/* Opportunity pages */}
              <Route path={`${l('ROUTE_CAUSES')}/:opportunityId`} element={<Opportunity />} />
              <Route
                path={`${l('ROUTE_CAUSES')}/:opportunityId/feedback`}
                element={<VolunteerFeedback />}
              />

              {/* Onboarding */}
              <Route path="/join" element={<JoinBrowniePoints />} />
              <Route path="/onboarding" element={<OnboardingLayout />}>
                <Route index element={<CompleteOnboarding />} />
              </Route>

              {/* Feedback */}
              <Route path={`${l('ROUTE_OPP')}-feedback`} element={<VolunteerFeedback />} />
              <Route path="/host-feedback" element={<HostFeedback />} />

              {/* auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/join-growsmart" element={<Navigate to="/set-password" replace />} />
              <Route path="/reset-password" element={<PasswordReset />} />
              <Route path="/set-password" element={<PasswordSetup />} />
              <Route path="/confirm-email" element={<EmailConfirmation />} />
              <Route path="/probation" element={<Probation />} />
              <Route path="/dashboard/login" element={<Navigate to="/login" replace />} />

              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="email-activity" element={<DashboardEmailActivity />} />
                <Route path="account" element={<DashboardAccount />} />
                <Route
                  path="account/notification-preferences"
                  element={<DashboardUserAccountPrefs />}
                />
                <Route path="rounds" element={<DashboardOpportunities />} />
                <Route path="rounds/:opportunityId" element={<DashboardRound />} />
                <Route path="rounds/new" element={<DashboardNewRounds />} />
                <Route path="workshops" element={<DashboardOpportunities />} />
                <Route path="workshops/:opportunityId" element={<DashboardWorkshop />} />
                <Route path="workshops/new" element={<DashboardNewWorkshops />} />

                <Route path="manage-person/:personId" element={<DashboardUpdateMembership />} />
                <Route path="people/:personId" element={<DashboardPersonActivity />} />
                <Route path="people" element={<DashboardPeopleIndex />} />
                <Route path="people/new" element={<DashboardNewPeople />} />

                <Route path="competitions" element={<DashboardCampaigns />} />
                <Route path="competitions/new" element={<DashboardNewCampaign />} />
                <Route path="competitions/:campaignId" element={<DashboardCampaign />} />

                <Route path="institutions" element={<DashboardInstitutions />} />
                <Route path="institutions/new" element={<DashboardInstitutionNew />} />
                <Route path="institutions/:institutionId" element={<DashboardInstitution />} />

                <Route path="organisations" element={<DashboardOrganizations />} />
                <Route path="organisations/new" element={<DashboardNewOrganization />} />
                <Route path="organisations/:organisationId" element={<DashboardOrganization />} />
                <Route
                  path={`organisations/:organisationId/members`}
                  element={<DashboardOrganisationMembers />}
                />
                <Route
                  path={`organisations/:organisationId/learners`}
                  element={<DashboardSchoolLearners />}
                />

                <Route path="mentors" element={<GrowsmartMentors />} />
                <Route path="rsvps" element={<GrowsmartRsvps />} />
                <Route path="registrations" element={<GrowsmartRegistrations />} />
                <Route path="rounds/:roundId/manage-round" element={<GrowsmartManageRound />} />
                <Route path="enrolments" element={<GrowsmartEnrolments />} />
                <Route path="enrolments/:enrolmentId" element={<GrowsmartEnrolment />} />
                <Route path="sponsorships" element={<GrowsmartSponsorships />} />
                <Route path="sponsorships/new" element={<GrowsmartSponsorshipNew />} />
                <Route path="sponsorships/:sponsorshipId" element={<GrowsmartSponsorship />} />
                <Route path="scholarships" element={<GrowsmartScholarships />} />
                <Route path="scholarships/new" element={<GrowsmartScholarshipNew />} />
                <Route path="scholarships/:scholarshipId" element={<GrowsmartScholarship />} />
                <Route
                  path="scholarships/:scholarshipId/apply"
                  element={<GrowsmartScholarshipNewApplication />}
                />
                <Route
                  path="scholarships/:scholarshipId/applications"
                  element={<GrowsmartScholarshipApplications />}
                />
                <Route
                  path="scholarships/:scholarshipId/applications/:scholarshipApplicationId"
                  element={<DashboardScholarshipApplication />}
                />
                <Route
                  path={`competitions/:campaignId/province-schools`}
                  element={<GrowsmartInviteSchools />}
                />
                <Route
                  path={`competitions/:campaignId/competition-schools`}
                  element={<GrowsmartCompetitionOverview />}
                />
                <Route
                  path={`competitions/:campaignId/hardcopy-submissions`}
                  element={<GrowsmartHardcopySubmissions />}
                />
                <Route path=":misc" element={<DashboardNotFound />} />
              </Route>

              <Route path="/onboard-mentor" element={<OnboardMentor />} />
              <Route path="/onboard-principal" element={<OnboardPrincipal />} />

              <Route
                path="/gs/decline-invite/:inviteToken"
                element={<GrowsmartInvitationDecline />}
              />
              <Route path="/gs/parent-info/:token" element={<GrowsmartParentInfo />} />

              <Route path="/values" element={<Values />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </NavigationProvider>
        </Router>
      </Fragment>
    </Provider>
  </ThemeProvider>
)

export default App
